import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore } from '../stores/useEditorStore';
import { fillCircle, strokeRectWithRotation } from '../utils/canvas';
import { type Bounds, type BoundsWithCenter } from '../utils/editor/bounds';
import { point, pointRotateRads, type Point } from '../utils/math';

import { getCommonBounds, getElementBounds } from './bound';
import { getElementHitThreshold, pointOnLineSegment } from './collision';
import { getConfig } from './config';
import { getSelectionAbsoluteCoords, type Coords } from './coords';
import { type GenericElement } from './element';
import { getPlugin, resolveBooleanOrFunction } from './plugin';
import { type LineSegment } from './shapes';

export type TransformHandle = Bounds;
export type TransformHandleDirection =
  | 'e'
  | 'n'
  | 'ne'
  | 'nw'
  | 's'
  | 'se'
  | 'sw'
  | 'w';
export type TransformHandleType = 'rotation' | TransformHandleDirection;
export type TransformHandles = Partial<{
  [T in TransformHandleType]: TransformHandle;
}>;

export const getElementsWithinSelection = () => {
  const state = useEditorRuntimeStore.getState();
  const { elements } = useEditorStore.getState();
  const { limitSelectionToIds } = useEditorRuntimeStore.getState();

  if (!state.selectionElement) return [];

  const [selectionX1, selectionY1, selectionX2, selectionY2] =
    getSelectionAbsoluteCoords(state.selectionElement);

  const elementsInSelection = Array.from(elements.values()).filter(
    (element) => {
      if (!element.isVisible) return false;
      if (element.type === 'boolean') return false;

      if (
        limitSelectionToIds.length &&
        !limitSelectionToIds.includes(element.id)
      ) {
        return false;
      }

      const [elementX1, elementY1, elementX2, elementY2] =
        getElementBounds(element);

      return (
        selectionX1 <= elementX1 &&
        selectionY1 <= elementY1 &&
        selectionX2 >= elementX2 &&
        selectionY2 >= elementY2
      );
    },
  );

  return elementsInSelection;
};

/**
 * Merges newly selected element IDs into the current selection,
 * preserve existing order and append new selected IDs at the end.
 */
export const mergeSelectionIds = (
  newIds: string[],
  currentIds: string[],
): string[] => {
  const newSet = new Set(newIds);
  const currentSet = new Set(currentIds);

  const preserved = currentIds.filter((id) => newSet.has(id));
  const added = newIds.filter((id) => !currentSet.has(id));

  return [...preserved, ...added];
};

export const getSelectedElements = () => {
  const { elements, selectedElementIds } = useEditorStore.getState();
  const { limitSelectionToIds } = useEditorRuntimeStore.getState();

  return selectedElementIds
    .filter(
      (id) => limitSelectionToIds.includes(id) || !limitSelectionToIds.length,
    )
    .map((id) => elements.get(id))
    .filter(Boolean)
    .filter((element) => element.type !== 'boolean');
};

export const renderSelectionElement = (
  element: GenericElement,
  context: CanvasRenderingContext2D,
) => {
  const config = getConfig();
  const { scrollX, scrollY, zoom } = useEditorRuntimeStore.getState();

  context.save();
  context.translate(element.x + scrollX, element.y + scrollY);
  context.fillStyle = config.selection.fillStyle;

  const offset = 0.5 / zoom.value;

  context.fillRect(offset, offset, element.width, element.height);
  context.lineWidth = 1 / zoom.value;
  context.strokeStyle = config.selection.strokeStyle;
  context.strokeRect(offset, offset, element.width, element.height);

  context.restore();
};

export const renderSelectionBorder = (
  context: CanvasRenderingContext2D,
  element: GenericElement,
) => {
  const state = useEditorRuntimeStore.getState();
  const config = getConfig();

  const [elementX1, elementY1, elementX2, elementY2, cx, cy] = getElementBounds(
    element,
    {
      withRotation: false,
    },
  );

  const elementWidth = elementX2 - elementX1;
  const elementHeight = elementY2 - elementY1;

  const padding = config.selection.handleSpacing * 2;

  const linePadding = padding / state.zoom.value;
  const lineWidth = 8 / state.zoom.value;
  const spaceWidth = 4 / state.zoom.value;

  context.save();
  context.translate(state.scrollX, state.scrollY);
  context.lineWidth = 1 / state.zoom.value;

  context.strokeStyle = config.selection.strokeStyle;
  context.lineDashOffset = lineWidth + spaceWidth;

  strokeRectWithRotation(
    context,
    elementX1 - linePadding,
    elementY1 - linePadding,
    elementWidth + linePadding * 2,
    elementHeight + linePadding * 2,
    cx,
    cy,
    element.angle,
  );

  context.restore();
};

export const renderMultiSelectionBorder = (
  context: CanvasRenderingContext2D,
  elements: GenericElement[],
): BoundsWithCenter => {
  const config = getConfig();
  const state = useEditorRuntimeStore.getState();

  const dashedLinePadding =
    (config.selection.handleSpacing * 2) / state.zoom.value;

  const [x1, y1, x2, y2, cx, cy] = getCommonBounds(elements);
  const initialLineDash = context.getLineDash();

  context.setLineDash([2 / state.zoom.value]);
  const lineWidth = context.lineWidth;
  context.lineWidth = 1 / state.zoom.value;
  context.strokeStyle = config.selection.strokeStyle;

  strokeRectWithRotation(
    context,
    x1 - dashedLinePadding,
    y1 - dashedLinePadding,
    x2 - x1 + dashedLinePadding * 2,
    y2 - y1 + dashedLinePadding * 2,
    cx,
    cy,
    0,
  );

  context.lineWidth = lineWidth;
  context.setLineDash(initialLineDash);

  return [x1, y1, x2, y2, cx, cy];
};

export const generateTransformHandle = (
  x: number,
  y: number,
  width: number,
  height: number,
  cx: number,
  cy: number,
  angle: number,
): TransformHandle => {
  const [xx, yy] = pointRotateRads(
    point(x + width / 2, y + height / 2),
    point(cx, cy),
    angle,
  );
  return [xx - width / 2, yy - height / 2, width, height];
};

export const getTransformHandlesFromCoords = (
  [x1, y1, x2, y2, cx, cy]: Coords,
  angle: number,
): TransformHandles => {
  const config = getConfig();
  const state = useEditorRuntimeStore.getState();

  const size = config.selection.handleSize;
  const handleWidth = size / state.zoom.value;
  const handleHeight = size / state.zoom.value;

  const handleMarginX = size / state.zoom.value;
  const handleMarginY = size / state.zoom.value;

  const width = x2 - x1;
  // const height = y2 - y1;
  const dashedLineMargin = config.selection.handleMargin / state.zoom.value;
  const centeringOffset =
    (size - config.selection.handleSpacing * 2) / (2 * state.zoom.value);

  const transformHandles: TransformHandles = {
    ne: generateTransformHandle(
      x2 + dashedLineMargin - centeringOffset,
      y1 - dashedLineMargin - handleMarginY + centeringOffset,
      handleWidth,
      handleHeight,
      cx,
      cy,
      angle,
    ),
    nw: generateTransformHandle(
      x1 - dashedLineMargin - handleMarginX + centeringOffset,
      y1 - dashedLineMargin - handleMarginY + centeringOffset,
      handleWidth,
      handleHeight,
      cx,
      cy,
      angle,
    ),
    rotation: generateTransformHandle(
      x1 + width / 2 - handleWidth / 2,
      y1 -
        dashedLineMargin -
        handleMarginY +
        centeringOffset -
        config.selection.rotationResizeHandleGap / state.zoom.value,
      handleWidth,
      handleHeight,
      cx,
      cy,
      angle,
    ),
    se: generateTransformHandle(
      x2 + dashedLineMargin - centeringOffset,
      y2 + dashedLineMargin - centeringOffset,
      handleWidth,
      handleHeight,
      cx,
      cy,
      angle,
    ),
    sw: generateTransformHandle(
      x1 - dashedLineMargin - handleMarginX + centeringOffset,
      y2 + dashedLineMargin - centeringOffset,
      handleWidth,
      handleHeight,
      cx,
      cy,
      angle,
    ),
  };

  // For now we don't support mobile handles. If we do, we need to add
  // transformation handles for the sides as well.

  return transformHandles;
};

export const renderTransformHandles = (
  context: CanvasRenderingContext2D,
  transformHandles: TransformHandles,
) => {
  const config = getConfig();
  const state = useEditorRuntimeStore.getState();

  Object.keys(transformHandles).forEach((key) => {
    const transformHandle = transformHandles[key as TransformHandleType];

    if (transformHandle !== undefined) {
      const [x, y, width, height] = transformHandle;

      context.save();
      context.lineWidth = 1 / state.zoom.value;
      context.strokeStyle = config.selection.strokeStyle;
      context.fillStyle = config.selection.transformFillStyle;

      if (key === 'rotation') {
        fillCircle(context, x + width / 2, y + height / 2, width / 2);
      } else {
        context.beginPath();
        context.roundRect(x, y, width, height, 2 / state.zoom.value);
        context.fill();
        context.stroke();
      }

      context.restore();
    }
  });
};

export const isInsideTransformHandle = (
  transformHandle: TransformHandle,
  x: number,
  y: number,
) => {
  return (
    x >= transformHandle[0] &&
    x <= transformHandle[0] + transformHandle[2] &&
    y >= transformHandle[1] &&
    y <= transformHandle[1] + transformHandle[3]
  );
};

const getSelectionBorders = (
  [x1, y1]: Point,
  [x2, y2]: Point,
  center: Point,
  angle: number,
) => {
  const topLeft = pointRotateRads(point(x1, y1), center, angle);
  const topRight = pointRotateRads(point(x2, y1), center, angle);
  const bottomLeft = pointRotateRads(point(x1, y2), center, angle);
  const bottomRight = pointRotateRads(point(x2, y2), center, angle);

  return {
    e: [topRight, bottomRight],
    n: [topLeft, topRight],
    s: [bottomRight, bottomLeft],
    w: [bottomLeft, topLeft],
  };
};

export const resizeTestFromCoords = (
  [x1, y1, x2, y2, cx, cy]: Coords,
  [cursorX, cursorY]: Point,
  angle: number,
): null | TransformHandleType => {
  const config = getConfig();
  const transformHandles = getTransformHandlesFromCoords(
    [x1, y1, x2, y2, cx, cy],
    angle,
  );

  const found = Object.keys(transformHandles).find((key) => {
    const transformHandle =
      transformHandles[key as Exclude<TransformHandleType, 'rotation'>]!;
    return (
      transformHandle &&
      isInsideTransformHandle(transformHandle, cursorX, cursorY)
    );
  });

  if (found) return found as TransformHandleType;

  const state = useEditorRuntimeStore.getState();

  const SPACING = config.selection.sideResizingThreshold / state.zoom.value;
  const sides = getSelectionBorders(
    point(x1 - SPACING, y1 - SPACING),
    point(x2 + SPACING, y2 + SPACING),
    point(cx, cy),
    angle,
  );

  for (const [dir, side] of Object.entries(sides)) {
    if (
      pointOnLineSegment(point(cursorX, cursorY), side as LineSegment, SPACING)
    ) {
      return dir as TransformHandleType;
    }
  }

  return null;
};

export const isHittingAnySelectedElementHandle = (
  [cursorX, cursorY]: Point,
  selectedElements: GenericElement[],
): null | TransformHandleType => {
  if (selectedElements.length === 0) return null;

  const config = getConfig();
  const state = useEditorRuntimeStore.getState();
  const elementState = useEditorStore.getState();

  if (selectedElements.length > 1) {
    const coords = getCommonBounds(selectedElements);
    return resizeTestFromCoords(coords, [cursorX, cursorY], 0);
  }

  const element = selectedElements[0];
  if (element.isLocked) return null;

  const plugin = getPlugin(element.type);

  if (
    plugin.selection?.getTransformHandles &&
    plugin.transformation?.resizeTestFromBounds
  ) {
    const resize = plugin.transformation.resizeTestFromBounds(
      element,
      [cursorX, cursorY],
      { config, state },
    );
    if (resize) return resize as TransformHandleType;
  }

  const shouldHideDefaultTransformHandles = resolveBooleanOrFunction(
    plugin.selection?.hideDefaultTransformHandles,
    { elementState },
  );

  if (!shouldHideDefaultTransformHandles) {
    const coords = getElementBounds(element, { withRotation: false });
    return resizeTestFromCoords(coords, [cursorX, cursorY], element.angle ?? 0);
  }

  return null;
};

export const isHittingCommonBoundingBoxOfSelectedElements = (
  [pointerX, pointerY]: Point,
  selectedElements: GenericElement[],
) => {
  if (selectedElements.length < 2) return false;

  const threshold = getElementHitThreshold();
  const [x1, y1, x2, y2] = getCommonBounds(selectedElements);

  return isPointInBounds([pointerX, pointerY], [x1, y1, x2, y2], threshold);
};

export const isPointInBounds = (
  [pointerX, pointerY]: Point,
  bounds: Bounds | BoundsWithCenter,
  threshold = 0,
) => {
  const [x1, y1, x2, y2] = bounds;

  return (
    pointerX > x1 - threshold &&
    pointerX < x2 + threshold &&
    pointerY > y1 - threshold &&
    pointerY < y2 + threshold
  );
};

export const renderHoverBorder = (
  context: CanvasRenderingContext2D,
  element: GenericElement,
) => {
  const plugin = getPlugin(element.type);
  const state = useEditorRuntimeStore.getState();
  const config = getConfig();

  if (plugin.selection?.renderHoverBorder) {
    plugin.selection.renderHoverBorder(context, element, {
      config,
      state,
    });
    return;
  }

  const bounds = getElementBounds(element, { withRotation: false });
  const [x1, y1, x2, y2] = bounds;

  const elementWidth = x2 - x1;
  const elementHeight = y2 - y1;

  context.save();

  context.translate(state.scrollX, state.scrollY);

  context.lineWidth = config.hover.lineWidth / state.zoom.value;
  context.strokeStyle = config.hover.strokeStyle;
  context.strokeRect(x1, y1, elementWidth, elementHeight);
  context.restore();
};
